import React, { useState } from 'react';
import { Volume2, Play, X } from 'lucide-react';
import { VOICE_PRESETS, useVoicePreset, speakWithPreset } from '../utils/voicePresets';

/**
 * Voice preset selector — shown as a floating button or inline.
 * Lets user preview + choose a kawaii animal voice.
 */
export default function VoicePresetPicker({ compact = false }) {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useVoicePreset();

  const preview = (p) => {
    speakWithPreset(
      `Hej! Jag är ${p.label}. Så här låter jag när jag läser upp dina anteckningar.`,
      p
    );
  };

  const pick = (id) => {
    setCurrent(id);
    const p = VOICE_PRESETS[id];
    speakWithPreset(`${p.emoji} Nu läser ${p.label} upp åt dig!`, p);
    setTimeout(() => setOpen(false), 1200);
  };

  if (compact) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="neo-btn px-2 py-1 text-xs font-bold bg-white flex items-center gap-1"
        data-testid="voice-picker-compact"
        title={`Röst: ${current.label}`}
      >
        <span>{current.emoji}</span>
        <span className="hidden sm:inline">{current.label}</span>
      </button>
    );
  }

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="neo-btn px-3 py-2 bg-[#E8D5FF] text-sm font-bold flex items-center gap-2"
        data-testid="voice-picker-open"
      >
        <Volume2 className="w-4 h-4" />
        <span>{current.emoji} {current.label}</span>
      </button>

      {open && (
        <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="neo-card bg-white w-full max-w-md max-h-[85vh] overflow-y-auto p-4" onClick={(e) => e.stopPropagation()} data-testid="voice-picker-modal">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-['Fredoka'] font-bold text-lg flex items-center gap-2">
                <Volume2 className="w-5 h-5" /> Välj din röst
              </h3>
              <button onClick={() => setOpen(false)} className="neo-btn p-1.5 bg-white" data-testid="voice-picker-close">
                <X className="w-4 h-4" />
              </button>
            </div>
            <p className="text-xs opacity-70 mb-3 italic">Klicka "Lyssna" för att förhandsvisa → klicka kortet för att välja 🐾</p>
            <div className="grid grid-cols-1 gap-2">
              {Object.values(VOICE_PRESETS).map((p) => {
                const active = current.id === p.id;
                return (
                  <div
                    key={p.id}
                    className={`neo-card p-3 cursor-pointer flex items-center gap-3 transition-all ${active ? 'bg-[#B9FBC0] ring-2 ring-black' : 'bg-white hover:bg-[#FFF8E7]'}`}
                    onClick={() => pick(p.id)}
                    data-testid={`voice-preset-${p.id}`}
                  >
                    <div className="text-3xl flex-shrink-0">{p.emoji}</div>
                    <div className="flex-1 min-w-0">
                      <p className="font-['Fredoka'] font-bold text-sm">{p.label}</p>
                      <p className="text-[10px] opacity-60">
                        {p.pitch > 1.2 ? 'Ljus' : p.pitch < 0.8 ? 'Djup' : 'Mellan'} · {p.rate > 1.1 ? 'Snabb' : p.rate < 0.85 ? 'Långsam' : 'Normal'}
                      </p>
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); preview(p); }}
                      className="neo-btn px-2 py-1 bg-white text-xs font-bold flex items-center gap-1"
                      data-testid={`voice-preview-${p.id}`}
                    >
                      <Play className="w-3 h-3" /> Lyssna
                    </button>
                  </div>
                );
              })}
            </div>
            <p className="text-[10px] opacity-50 text-center mt-3 italic">
              Rösten används överallt där du klickar "läs upp"-knappen.
            </p>
          </div>
        </div>
      )}
    </>
  );
}
